import { BaseEditViewStore, BaasicDropdownStore } from 'core/stores';
import { FacilityOrderTemplatePatientCreateForm } from 'application/facility-order/forms';
import { FacilityOrderService } from 'application/facility-order/services';
import { PatientService } from 'application/patient/services';
import { PatientDetailService, DiagnosisCodeService, DoctorService, InsuranceService } from 'application/import/services';
import { SubCategoryService } from 'application/product/services';
import { LookupService } from "common/services";
import { action, observable, runInAction } from 'mobx';
import _ from 'lodash';
import { ModalParams } from 'core/models';
import moment from 'moment';
import { subCategoriesSort } from 'core/utils';

class FacilityOrderTemplatePatientCreateViewStore extends BaseEditViewStore {
    form = new FacilityOrderTemplatePatientCreateForm({
        onSuccess: async (form) => {
            await this.onSubmit(form.values());
        }
    });

    @observable accessor disableRequiredFields = true;
    @observable accessor oldPrimaryInsuranceDisplayed = false;
    @observable accessor isCheckingForExistingPatient = false;

    constructor(rootStore) {
        const facilityOrderId = rootStore.routerStore.routerState.params.facilityOrderId;

        super(rootStore, {
            name: 'facility-order-template-patient-create',
            Form: FacilityOrderTemplatePatientCreateForm,
            authorization: 'facilityOrderSection.create',
            actions: {},
            FormClass: FacilityOrderTemplatePatientCreateForm,
        });

        this.patientService = new PatientService(rootStore.application.baasic.apiClient);
        this.patientDetailService = new PatientDetailService(rootStore.application.baasic.apiClient);
        this.diagnosisCodeService = new DiagnosisCodeService(rootStore.application.baasic.apiClient);
        this.doctorService = new DoctorService(rootStore.application.baasic.apiClient);
        this.insuranceService = new InsuranceService(rootStore.application.baasic.apiClient);
        this.subCategoryService = new SubCategoryService(this.rootStore.application.baasic.apiClient);
        this.lookupService = new LookupService(rootStore.application.baasic.apiClient);

        this.titleData = null;
        this.states = null;
        this.facilityOrderId = facilityOrderId;

        this.setTitleAndTitleData(facilityOrderId);

        this.isDisabled = () => {
            return this.disableRequiredFields;
        }

        this.existingPatientDataModal = new ModalParams({ onClose: null });
        this.openExistingPatientDataModal = (patients) => {
            this.existingPatientDataModal.open({
                patients: patients,
                facilityOrderId: facilityOrderId,
                titleData: this.titleData
            });
        }

        this.contactRelationshipStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{ id: 1, name: 'None' }, { id: 2, name: 'Child' }, { id: 3, name: 'Friend' }, { id: 4, name: 'Grandparent' }, { id: 5, name: 'Other' }, { id: 6, name: 'Parent' }, { id: 7, name: 'Self' }, { id: 8, name: 'Spouse' }];
            }
        });

        this.stateStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'abrv',
            filterable: true
        }, {
            fetchFunc: async (term = "") => {
                await this.getStates();
                const stateData = this.states.filter((s) => s.name.toLowerCase().includes(term.toLowerCase()));

                return stateData;
            },
        });

        this.genderStore = new BaasicDropdownStore({
            textField: 'name',
            dataItemKey: 'name',
            filterable: true
        }, {
            fetchFunc: () => {
                return [{ id: 1, name: 'Male' }, { id: 2, name: 'Female' }];
            }
        });

        this.primaryInsuranceStore = this.constructInsuranceDropdownStore('primary');
        this.oldPrimaryInsuranceStore = this.constructInsuranceDropdownStore('oldPrimary');
        this.secondaryInsuranceStore = this.constructInsuranceDropdownStore('secondary');
        this.diagnosisCodesStore = this.constructDiagnosisCodesDropdownsStore();
        this.doctorStore = this.constructDoctorDropdownsStore();
        this.subCategoryStore = this.constructSubCategoryDropdownStore();
    }

    @action.bound
    async getStates() {
        const response = await this.lookupService.getLookups("State");

        runInAction(() => {
            this.states = response.state;
        });
    }

    @action.bound
    toggleOldPrimaryInsurance() {
        if (this.oldPrimaryInsuranceDisplayed) {
            this.form.$('oldPrimaryInsuranceName').value = null;
            this.form.$('oldPrimaryInsurancePolicyNumber').value = null;
            this.form.$('oldPrimaryInsuranceStartDate').value = null;
            this.form.$('oldPrimaryInsuranceEndDate').value = null;

            this.form.$('oldPrimaryInsurancePolicyNumber').set('rules', null);
            this.form.$('oldPrimaryInsuranceStartDate').set('rules', null);
            this.form.$('oldPrimaryInsuranceEndDate').set('rules', null);

            this.oldPrimaryInsuranceDisplayed = false;
        } else {
            this.oldPrimaryInsuranceDisplayed = true;
        }
    }

    @action.bound
    async invalidateInsurances() {
        this.form.setFieldsDisabled(true);
        try {
            await this.insuranceService.invalidate();
            this.rootStore.notificationStore.success("Insurance list successfully refreshed.");
        }
        catch (err) {
            this.rootStore.notificationStore.error("Error occured on while trying to refresh Insurance list.");
        }

        this.form.setFieldsDisabled(false);
    }

    @action.bound
    constructInsuranceDropdownStore(prefix) {
        return new BaasicDropdownStore({
            textField: 'insuranceName',
            dataItemKey: 'insuranceName',
            filterable: true
        }, {
            fetchFunc: async (term) => {
                const response = await this.insuranceService.find({ searchQuery: term });
                return response.data.item;
            },
            onChange: (insurance) => {
                if (insurance) {
                    this.form.$(prefix + 'InsurancePolicyNumber').set('rules', 'required');
                    this.form.$(prefix + 'InsuranceStartDate').set('rules', 'required');
                    this.form.$(prefix + 'InsuranceStartDate').value = moment('1980-01-01', 'YYYY-MM-DD');
                    if (prefix == 'oldPrimary')
                        this.form.$(prefix + 'InsuranceEndDate').set('rules', 'required');
                }
            }
        });
    }
    @action.bound
    capitalizeFirstLetter() {
        let entered = this.form.$('firstName').value;
        let firstLetter = entered.charAt(0).toUpperCase();
        let rest = entered.substring(1);
        this.form.$('firstName').value = (firstLetter + rest);

        let entered2 = this.form.$('lastName').value;
        let firstLetter2 = entered2.charAt(0).toUpperCase();
        let rest2 = entered2.substring(1);
        this.form.$('lastName').value = (firstLetter2 + rest2);
    }

    @action.bound
    constructDiagnosisCodesDropdownsStore() {
        return new BaasicDropdownStore({
            textField: 'code',
            dataItemKey: 'diagnosisCodeId',
            filterable: true,
            multi: true,
        }, {
            fetchFunc: async (term) => {
                const response = await this.diagnosisCodeService.find({ searchQuery: term, rpp: 20, isBillable: true });

                let mappedResult = _.map(response.data.item, item => {
                    return _.extend(item, { diagnosisCodeId: item.id });
                });
                return mappedResult;
            }
        });
    }

    @action.bound
    constructSubCategoryDropdownStore() {
        return new BaasicDropdownStore({}, {
            fetchFunc: async (term) => {
                const subCategorysAbrv = ["G-Tube", "Oral-Supplements", "Enteral", "Respiratory---trachs-supply", "Trach-Tube", "Urologicals", "Ostomy-Products", "Wound-Care"];
                const response = await this.subCategoryService.find({ searchQuery: term, rpp: 100, sort: "itemCategory.name|asc", embed: "ItemCategory" });

                let data = subCategoriesSort(response.data.item.map(i => { return { ...i, name: `${i.name}` } }));
                let filteredSubCategories = _.filter(data, i => {
                    return subCategorysAbrv.some(s => i.abrv === s);
                });

                return filteredSubCategories;
            }
        });
    }

    @action.bound
    constructDoctorDropdownsStore() {
        return new BaasicDropdownStore({
            textField: 'orderingDoctor',
            dataItemKey: 'orderingDoctorId',
            filterable: true,
            returnObject: true
        }, {
            fetchFunc: async (term) => {
                const response = await this.doctorService.find({ searchQuery: term });

                _.extend(response.data,
                    {
                        item: _.map(response.data.item, (item => {
                            return _.extend(
                                item, {
                                orderingDoctor: (item.name + " - " + item.npi),
                                orderingDoctorId: item.id
                            }
                            );
                        }))
                    });
                return response.data.item;
            },
            onChange: (doctor) => {
                this.form.set({ orderingDoctor: doctor.orderingDoctor, orderingDoctorId: doctor.orderingDoctorId });
            }
        });
    }

    async setTitleAndTitleData(facilityOrderId) {
        const facilityOrderService = new FacilityOrderService(this.rootStore.application.baasic.apiClient);
        const facilityOrderResponse = await facilityOrderService.get(facilityOrderId, { embed: 'Facility' });

        const title = `${facilityOrderResponse.data.facility.name} - Create New Patient`;
        this.titleData = title;

        this.rootStore.viewStore.setNavigationOptions({
            title: title,
            hideColon: true
        });
    }

    @action.bound
    async checkForExistingBrightreePatient() {
        const { firstName, lastName, dateOfBirth } = this.form.values();

        if (firstName === '' || lastName === '' || dateOfBirth === '') {
            this.isCheckingForExistingPatient = false;
            this.rootStore.notificationStore.warning('Please enter all required fields.');
            return;
        }
        if(!this.form.$("dateOfBirth").isValid){
            this.rootStore.notificationStore.warning('Please enter a valid value for Date Of Birth.');
            return;
        }
        this.isCheckingForExistingPatient = true;

        const params = {
            firstName,
            lastName,
            dateOfBirth: moment(dateOfBirth).format("YYYY-MM-DD")
        };

        try {
            const patientResponse = await this.patientService.findPatients(params);

            if (patientResponse.item && patientResponse.item.length > 0) {
                this.openExistingPatientDataModal(patientResponse.item);
                this.disableRequiredFields = true;
            }
            else {
                this.rootStore.notificationStore.warning('Searched patient doesn\'t exist in Brightree. Please fill out the rest of the form to create a new one.');
                this.disableRequiredFields = false;
            }

            this.isCheckingForExistingPatient = false;
        }
        catch (err) {
            this.isCheckingForExistingPatient = false;
            this.rootStore.notificationStore.error('Error occured while trying to get brightree patient data.', err);
        }
    }

    @action.bound
    async onSubmit(data) {
        if (!data.ssn) {
            this.rootStore.modalStore.showConfirm(
                'The SSN field is empty. Do you want to proceed anyway?',
                async () => {
                    await this.createPatient(data);
                }
            );
        }
        else {
            await this.createPatient(data);
        }
    }
    
    @action.bound
    reloadPatient() {
        this.form.clear();
        this.disableRequiredFields = true;
        this.contactRelationshipStore.setValue(null);
        this.stateStore.setValue(null);
        this.genderStore.setValue(null);
        this.primaryInsuranceStore.setValue(null);
        this.oldPrimaryInsuranceStore.setValue(null);
        this.secondaryInsuranceStore.setValue(null);
        this.diagnosisCodesStore.setValue(null);
        this.doctorStore.setValue(null);
        this.subCategoryStore.setValue(null);
    }

    @action.bound
    async createPatient(data) {
        if (!this.form.isValid) {
            this.rootStore.notificationStore.warning('Please enter all required fields.');
            return;
        }

        const resource = {
            ...data,
            orderingDoctorId: data.orderingDoctorId === "" ? 0 : data.orderingDoctorId,
            facilityOrderId: this.facilityOrderId,
            dateOfBirth: moment(data.dateOfBirth).format("YYYY-MM-DD"),
            primaryInsuranceStartDate: data.primaryInsuranceStartDate && moment(data.primaryInsuranceStartDate).format("YYYY-MM-DD"),
            secondaryInsuranceStartDate: data.secondaryInsuranceStartDate && moment(data.secondaryInsuranceStartDate).format("YYYY-MM-DD"),
            secondaryInsuranceEndDate: data.secondaryInsuranceEndDate && moment(data.secondaryInsuranceEndDate).format("YYYY-MM-DD"),
            oldPrimaryInsuranceStartDate: data.oldPrimaryInsuranceStartDate && moment(data.oldPrimaryInsuranceStartDate).format("YYYY-MM-DD"),
            oldPrimaryInsuranceEndDate: data.oldPrimaryInsuranceEndDate && moment(data.oldPrimaryInsuranceEndDate).format("YYYY-MM-DD"),
        };

        try {
            var response = await this.patientService.createBrightreeAndLocalPatientAndInitializeTemplate(resource);
            let patientInfo = response.data;
            this.rootStore.routerStore.goTo('master.app.main.facility-order.facility-order-template.edit', { facilityOrderId: this.facilityOrderId, patientId: patientInfo.id, patientKey: patientInfo.patientKey });

            this.rootStore.notificationStore.success('Successfully created patient and patient templates.');
        }
        catch (err) {
            this.rootStore.notificationStore.error('Error occured while trying to create patient and template.', err);
        }
    }
}

export default FacilityOrderTemplatePatientCreateViewStore;
